import React, { useEffect, useState, useRef } from "react";
import { Menu, Tooltip, Row, Col, Form, Button } from "antd";
import InternalDetails from "./InternalDetails";
import BasicDetails from "./BasicDetails";
import ProductInformation from "./ProductInformation";
import { BsFillLockFill } from "react-icons/bs";
import useIntersectionObserver from "@react-hook/intersection-observer";
import ProductionDetails from "./ProductionDetails";
import "./styles.css";
import { gaEvent } from "analytics/analytics";
import * as GA from "../../../constants/analytics";

const TOTAL_TABS = 4;

const calc = (totalNumOfFields, maxFieldsNeededForATabToFillScreen) => {
  const k =
    totalNumOfFields <= maxFieldsNeededForATabToFillScreen
      ? maxFieldsNeededForATabToFillScreen
      : totalNumOfFields;
  return maxFieldsNeededForATabToFillScreen / k;
};

const getThreshold = (tabname, n = 0) => {
  if (window.innerHeight < 600) return 0;

  if (tabname === "basic") {
    if (n <= 1) return 0.8;
    return calc(n, 1);
  }
  if (tabname === "production") {
    return calc(n, 3);
  }
  if (tabname === "internal") {
    return calc(n, 1);
  }

  if (tabname === "production_detail") {
    return calc(n, 3);
  }
};

const filterCategoryData = (categoryData) => {
  const productFields = [];
  const internalFields = [];
  const basicFields = [];
  const vendorFields = [];
  const exclusiveFields = [];
  const productionFields = [];

  categoryData?.attributes.forEach((field) => {
    if (
      field?.attributes_id?.tabname === "production" ||
      field?.tabname === "production"
    )
      productFields.push(field);
    else if (
      field?.attributes_id?.tabname === "internal" ||
      field?.tabname === "internal"
    )
      internalFields.push(field);
    else if (
      field?.attributes_id?.tabname === "basic" ||
      field?.tabname === "basic"
    )
      basicFields.push(field);
    else if (
      field?.attributes_id?.tabname === "vendor" ||
      field?.tabname === "vendor"
    )
      vendorFields.push(field);
    else if (
      field?.attributes_id?.tabname === "exclusive" ||
      field?.tabname === "exclusive"
    )
      exclusiveFields.push(field);
    else if (
      field?.attributes_id?.tabname === "production_detail" ||
      field?.tabname === "production_detail"
    )
      productionFields.push(field);
  });

  return {
    productFields,
    internalFields,
    basicFields,
    vendorFields,
    exclusiveFields,
    productionFields,
  };
};

export default function StepsComponent({
  hideInfo,
  current,
  handleClick,
  onChangeField,
  getCategoryData,
  categoryData,
  allCategoryData,
  setAllCategoryData,
  handleFinish,
  handleNext,
  showTooltipOnFinish,
  page,
  loadingFinishButton,
  showModalVariant,
  variantsData,
  variantsCount,
  productId,
  setProductId,
  categoryId,
  setCategoryId,
  subcategory,
  setSubCategory,
  showVendorDetails,
  setVendorDetailsButton,
  showExclusiveDetails,
  setExclusiveDetailsButton,
  params,
  form,
  formData,
  files,
  sizeUnits,
  setSizeUnits,
}) {
  const [productFields, setProductFields] = useState(null);
  const [productionFields, setProductionFields] = useState(null);
  const [internalFields, setInternalFields] = useState(null);
  const [basicFields, setBasic] = useState(null);
  const [vendorFields, setVendorDetails] = useState(null);
  const [exclusiveFields, setExclusiveDetails] = useState(null);
  const internalRef = useRef();
  const basicRef = useRef();
  const productRef = useRef();
  const productionRef = useRef();

  const isBasicRefVisible = useIntersectionObserver(basicRef, {
    threshold: getThreshold("basic", basicFields?.length),
  }).isIntersecting;
  const isProductRefVisible = useIntersectionObserver(productRef, {
    threshold: getThreshold("production", productFields?.length),
  }).isIntersecting;
  const isInternalRedVisible = useIntersectionObserver(internalRef, {
    threshold: getThreshold(
      "internal",
      (internalFields?.length || 0) +
        (vendorFields?.length || 0) +
        (exclusiveFields?.length || 0)
    ),
  }).isIntersecting;

  const isProductionRefVisible = useIntersectionObserver(productionRef, {
    threshold: getThreshold("production_detail", productionFields?.length),
  }).isIntersecting;

  useEffect(() => {
    if (categoryData) {
      const {
        productFields,
        internalFields,
        basicFields,
        vendorFields,
        exclusiveFields,
        productionFields,
      } = filterCategoryData(categoryData);
      setProductFields(productFields);
      setInternalFields(internalFields);
      setBasic(basicFields);
      setVendorDetails(vendorFields);
      setExclusiveDetails(exclusiveFields);
      setProductionFields(productionFields);
    }
  }, [categoryData]);

  useEffect(() => {
    if (isBasicRefVisible && current !== "1") {
      handleClick({ key: "1" });
    } else if (isProductRefVisible && current !== "2" && !isBasicRefVisible) {
      handleClick({ key: "2" });
    } else if (isInternalRedVisible && current !== "3" && !isBasicRefVisible) {
      handleClick({ key: "3" });
    } else if (
      isProductionRefVisible &&
      current !== "4" &&
      !isBasicRefVisible
    ) {
      handleClick({ key: "4" });
    }
  }, [
    isBasicRefVisible,
    isProductRefVisible,
    isInternalRedVisible,
    isProductionRefVisible,
  ]);

  const TITLE_STYLE = {
    fontWeight: "bolder",
    fontSize: 14,
    paddingRight: 20,
    paddingLeft: 0,
  };

  const handleNextWithScroll = () => {
    if (current === "1") {
      productRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (current === "2") {
      internalRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (current === "3") {
      productionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToError = () => {
    //basicRef.current.scrollIntoView({ behavior: "smooth" });
    internalRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const handleFinishWithScroll = () => {
    handleFinish(scrollToError);
  };

  const renderFinishButton = () => {
    if (showTooltipOnFinish)
      return (
        <Tooltip
          title="Upload atleast one image to this product"
          trigger={["hover", "click"]}
          style={{ textAlign: "center" }}
          mouseLeaveDelay={2}
        >
          <Button
            style={{ width: 132, height: 40 }}
            disabled={hideInfo}
            type="primary"
            size="large"
            htmlType="submit"
            onClick={() => {
              gaEvent(
                GA.EVENT.CLICK,
                GA.CATEGORY.ADD_PRODUCT,
                GA.DESCRIPTION.ADD_PRODUCT_FINISH_DISABLED_BUTTON
              );
            }}
          >
            Finish
          </Button>
        </Tooltip>
      );
    return (
      <Button
        style={{ width: 132, height: 40 }}
        disabled={hideInfo}
        onClick={() => {
          handleFinishWithScroll();
          gaEvent(
            GA.EVENT.CLICK,
            GA.CATEGORY.ADD_PRODUCT,
            GA.DESCRIPTION.ADD_PRODUCT_FINISH_BUTTON
          );
        }}
        type="primary"
        size="large"
        loading={loadingFinishButton}
        htmlType="submit"
      >
        Finish
      </Button>
    );
  };

  return (
    <div>
      <Menu
        onClick={handleClick}
        selectedKeys={current}
        mode="horizontal"
        style={{ marginBottom: 20 }}
      >
        <Menu.Item
          key="1"
          onClick={() =>
            basicRef.current.scrollIntoView({ behavior: "smooth" })
          }
          style={TITLE_STYLE}
        >
          1. Basic Details
        </Menu.Item>
        <Menu.Item
          key="2"
          disabled={hideInfo}
          icon={hideInfo ? <BsFillLockFill /> : null}
          onClick={() =>
            productRef.current.scrollIntoView({ behavior: "smooth" })
          }
          style={TITLE_STYLE}
        >
          {hideInfo ? (
            <Tooltip title="Fill basic details">
              <span> 2. Product info</span>
            </Tooltip>
          ) : (
            " 2. Product info"
          )}
        </Menu.Item>

        <Menu.Item
          key="3"
          onClick={() =>
            internalRef.current.scrollIntoView({ behavior: "smooth" })
          }
          disabled={hideInfo}
          style={TITLE_STYLE}
          icon={hideInfo ? <BsFillLockFill /> : null}
        >
          {hideInfo ? (
            <Tooltip title="Fill basic details">
              <span> 3. Internal Details</span>
            </Tooltip>
          ) : (
            "  3. Internal Details"
          )}
        </Menu.Item>

        <Menu.Item
          key="4"
          onClick={() =>
            productionRef.current.scrollIntoView({ behavior: "smooth" })
          }
          disabled={hideInfo}
          style={TITLE_STYLE}
          icon={hideInfo ? <BsFillLockFill /> : null}
        >
          {hideInfo ? (
            <Tooltip title="Fill basic details">
              <span> 4. Production Details</span>
            </Tooltip>
          ) : (
            "  4. Production Details"
          )}
        </Menu.Item>
      </Menu>

      <div
        className="scroll-container"
        style={{ height: "60vh", overflowY: "scroll" }}
      >
        <span ref={basicRef}>
          <BasicDetails
            onChangeField={onChangeField}
            getCategoryData={getCategoryData}
            allCategoryData={allCategoryData}
            setAllCategoryData={setAllCategoryData}
            fields={basicFields}
            page={page}
            showModalVariant={showModalVariant}
            variantsData={variantsData}
            variantsCount={variantsCount}
            setProductId={setProductId}
            productId={productId}
            setCategoryId={setCategoryId}
            categoryId={categoryId}
            subcategory={subcategory}
            setSubCategory={setSubCategory}
            params={params}
            form={form}
          />
        </span>
         
        {
          <span ref={productRef} style={!hideInfo ? {} : { display: "none" }}>
            <ProductInformation
              fields={productFields}
              categoryId={categoryId}
              allCategoryData={allCategoryData}
              form={form}
              sizeUnits={sizeUnits}
              setSizeUnits={setSizeUnits}
            />
          </span>
        }
        {
          <span ref={internalRef} style={!hideInfo ? {} : { display: "none" }}>
            <InternalDetails
              fields={internalFields}
              vendorFields={vendorFields}
              showVendorDetails={showVendorDetails}
              setVendorDetailsButton={setVendorDetailsButton}
              exclusiveFields={exclusiveFields}
              showExclusiveDetails={showExclusiveDetails}
              setExclusiveDetailsButton={setExclusiveDetailsButton}
              form={form}
              formData={formData}
              files={files}
              categoryId={categoryId}
              allCategoryData={allCategoryData}
            />
          </span>
        }
        {
          <span
            ref={productionRef}
            style={!hideInfo ? {} : { display: "none" }}
          >
            <ProductionDetails
              fields={productionFields}
              categoryId={categoryId}
              allCategoryData={allCategoryData}
            />
          </span>
        }
      </div>
      <Col>
        <div
          style={{
            marginTop: 20,
            display: "flex",
            marginRight: 10,
            justifyContent: "flex-end",
          }}
        >
          {current === TOTAL_TABS.toString() ? (
            renderFinishButton()
          ) : (
            <Button
              style={{ height: 40, width: 132 }}
              onClick={() => {
                handleNextWithScroll();
                gaEvent(
                  GA.EVENT.CLICK,
                  GA.CATEGORY.ADD_PRODUCT,
                  GA.DESCRIPTION.ADD_PRODUCT_NEXT_BUTTON
                );
              }}
              disabled={hideInfo}
              type="primary"
              size="large"
            >
              Next
            </Button>
          )}
        </div>
      </Col>
    </div>
  );
}
